/**
 * RFC-172 Stage 2 — single questionnaire row.
 *
 * Renders one catalog blueprint's question with radio chip options and
 * (when the user selects PARTIAL/NO) a rationale textarea. Calls
 * `trpc.readinessQuestionnaire.submitAnswer` on selection — auto-save,
 * follow-up details use explicit Save buttons. Pre-fill state (verified by scanner / AI-
 * imported / cold) is rendered via the `state` prop.
 *
 * Rendered both inside the onboarding wizard's per-section screens
 * AND inside the post-onboarding readiness-questionnaire jump-anywhere
 * page (RFC-172 §4.7). The component is surface-agnostic — pass
 * `programId` + the blueprint payload from `getSections`, and the
 * row writes through the canonical fan-out helper.
 */
import { useEffect, useRef, useState } from 'react'
import {
  POLICY_EXCLUSION_OPTION_KEY,
  type PolicyQuestionScopeDTO,
  type PolicyQuestionAnswerMode,
  type PolicyExceptionRequestDTO,
  type PolicyQuestionDimensionFollowupDTO,
  type QuestionnaireRemediationPlanDTO,
  type QuestionnaireGapDraftDTO,
  type QuestionnaireDimensionStateEntryDTO,
  hasAlternativeControlDetails,
} from '@deepguide-ai/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge } from '@deepguide-ai/ui/badge'
import { useParams } from '@tanstack/react-router'
import {
  AlertTriangle,
  ExternalLink,
  ShieldCheck,
  Sparkles,
} from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { useAskAi } from '@/components/chat/useAskAi'
import { useOnboardingAssistant } from '@/components/screenata/onboarding/onboardingAssistant'
import { ProviderCheckDetailSheet } from '@/components/screenata/scan/ProviderCheckDetailSheet'
import { AssignCombobox } from '@/components/compliance/AssignCombobox'
import { useDebounce } from '@/hooks/useDebounce'
import { configForStatus } from '@/pages/tasks/taskColumns.config'

export type RowState = 'verified' | 'attested' | 'cold'

export type AttestationAnswerValue = 'YES' | 'PARTIAL' | 'NO' | 'NA' | 'UNKNOWN'

function formatAnswer(answer: AttestationAnswerValue): string {
  return answer === 'NA'
    ? 'Not applicable'
    : answer.charAt(0) + answer.slice(1).toLowerCase()
}

export function formatScannerConflict(input: {
  declaredAnswer: AttestationAnswerValue
  scannerAnswer: AttestationAnswerValue
  declarationSource: 'POLICY' | 'ATTESTATION'
}): string {
  const source = input.declarationSource === 'POLICY' ? 'policy' : 'attestation'
  return `Your ${source} says ${formatAnswer(input.declaredAnswer)}; scan shows ${formatAnswer(input.scannerAnswer)}. Please review.`
}

export interface QuestionnaireBlueprintForRow {
  id: string
  controlRef: string | null
  slotKey: string | null
  scopeKey: string | null
  policyScope?: PolicyQuestionScopeDTO
  includedInPolicy?: boolean
  policyTextWhenConfirmed?: string
  itemTitle?: string
  question: {
    answerMode?: PolicyQuestionAnswerMode
    id: string
    promptTemplate: string
    options?: Array<{
      key: string
      label: string
      hint?: string
      effect: 'attest' | 'generic_only' | 'reject'
      recommended?: boolean
      policyTextFromRationale?: boolean
      /** Canonical AttestationAnswer this option maps to. */
      verdict?: AttestationAnswerValue
    }>
  }
  /**
   * RFC-172 Stage 7 — When set, renders a checkbox per dimension on
   * PARTIAL/NO. Null/undefined for binary blueprints.
   */
  dimensions?: Array<{
    key: string
    label: string
    policyFragment: string
    required?: boolean
    /** Authored placeholder for the per-dim Remediate textarea. */
    remediationHint?: string
  }> | null
}

/**
 * RFC-172 Stage 7 — per-dimension state. Single 4-pill control per
 * dimension. Mirror of server PolicyBlueprintDimensionState.
 */
export type DimensionState = 'yes' | 'remediate' | 'compensate' | 'accept'
export type DimensionStateEntry = QuestionnaireDimensionStateEntryDTO
export type DimensionStateMap = Record<string, DimensionStateEntry>

export interface RowInitialState {
  state: RowState
  attestationAnswer: AttestationAnswerValue | null
  /**
   * The draft's raw `optionKey` when the row state came from a draft.
   * Required so the UI can rehydrate the pill selection for
   * bucket-style questions (RTO/RPO/SLA) where multiple option keys
   * map to the same attestationAnswer — a reverse-map would lose the
   * user's specific bucket pick.
   */
  optionKey?: string | null
  rationale: string | null
  /** RFC-172 Stage 7 — per-dim state map (yes/remediate/compensate/accept). */
  dimensionStates?: DimensionStateMap | null
  /**
   * RFC-172 Stage 7 / Phase E — provenance for imported answers.
   * When set, the row renders an [Imported] / [Imported · review]
   * badge indicating the answer came from a policy upload.
   */
  importMeta?: {
    extractionId: string
    source: 'imported' | 'imported-low-confidence'
    overallConfidence: number
    perDimConfidence?: Record<string, number>
    importedAt: string
    /** Slug of the uploaded policy template, when known. */
    policySlug?: string | null
    /** Human-readable title (e.g. "Access Control Policy"). */
    policyTitle?: string | null
    /** Top-level citations (binary blueprints). */
    citations?: Array<{ quote: string }>
    /** Per-dimension citations (dim'd blueprints). */
    citationsByDim?: Record<string, Array<{ quote: string }>>
    blockerNotes?: string[]
  } | null
  /** Source label for the badge (e.g. "Verified by Okta scan") */
  sourceLabel?: string
  scannerEvidence?: {
    answer: 'YES' | 'NO' | 'NA' | null
    observedAt: string
    runs: Array<{
      source: 'API_TEST_RUN' | 'COMPLIANCE_SCAN'
      id: string
      checkId: string | null
      statusId: string | null
      connectionId: string | null
      provider: string
      resultStatus: string
      finishedAt: string
    }>
    supportingRuns?: Array<{
      source: 'API_TEST_RUN' | 'COMPLIANCE_SCAN'
      id: string
      checkId: string | null
      statusId: string | null
      connectionId: string | null
      provider: string
      resultStatus: string
      finishedAt: string
    }>
  } | null
  conflict?: {
    scannerAnswer: AttestationAnswerValue
    declaredAnswer: AttestationAnswerValue
    declarationSource: 'POLICY' | 'ATTESTATION'
    policySlug?: string | null
    policyTitle?: string | null
    citations?: string[]
  } | null
  /**
   * RFC-172 — Security Profile vendor hint. When set, the row
   * badges the matching option as "Suggested by your stack" without
   * pre-selecting it. The user still clicks to confirm.
   */
  vendorHint?: { optionKey: string; reason: string } | null
}

interface ReadinessQuestionnaireRowProps {
  programId: string
  blueprint: QuestionnaireBlueprintForRow
  initial: RowInitialState
  /**
   * 1-based position of this question within its section. Rendered as a
   * stable, referenceable ordinal (e.g. "2") so users can cite a
   * specific question in the support channel. Sub-questions extend it
   * as "2.1", "2.2", … Derived from the blueprint's order in
   * getSections, which is stable for a given catalog version.
   */
  questionNumber?: number
  onAnswered?: (
    blueprintId: string,
    attestationAnswer: AttestationAnswerValue,
    optionKey?: string,
    /**
     * 'hydration' — fired from the mount/refetch effect that syncs local
     * state from a server-hydrated answer (the wizard uses it to seed its
     * completion map). 'user' (default) — an actual pick. Dialog hosts MUST
     * ignore 'hydration': treating it as a user action made the "Change"
     * dialog commit-and-close the instant it opened for any answered
     * question.
     */
    source?: 'hydration' | 'user'
  ) => void
  onDraftSavingChange?: (blueprintId: string, saving: boolean) => void
}

export function ReadinessQuestionnaireRow({
  programId,
  blueprint,
  initial,
  questionNumber,
  onAnswered,
  onDraftSavingChange,
}: ReadinessQuestionnaireRowProps) {
  const queryClient = useQueryClient()
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId?: string
  }
  const practiceQuestion = blueprint.question.answerMode === 'requirement'
  const practiceInScope = blueprint.policyScope?.applicable !== false
  const draftSaveCountRef = useRef(0)
  const hasDimensions = Boolean(
    blueprint.dimensions && blueprint.dimensions.length > 0
  )

  const [selectedOptionKey, setSelectedOptionKey] = useState<string | null>(
    null
  )
  const [parentRationale, setParentRationale] = useState(initial.rationale ?? '')
  const [parentSaveError, setParentSaveError] = useState<string | null>(null)
  const [parentDetailsSaved, setParentDetailsSaved] = useState(false)
  const [pendingKey, setPendingKey] = useState<string | null>(null)
  const [, setCommittedKey] = useState<string | null>(null)
  const [selectedPostureRun, setSelectedPostureRun] = useState<
    NonNullable<RowInitialState['scannerEvidence']>['runs'][number] | null
  >(null)
  // RFC-172 Stage 7 — per-dim 4-state map (yes/remediate/compensate/accept).
  const [dimStates, setDimStates] = useState<DimensionStateMap>(() => ({
    ...(initial.dimensionStates ?? {}),
  }))

  // Hydrate selectedOptionKey from initial state. For binary blueprints
  // we match attestationAnswer back to a canonical option key. For dim'd
  // blueprints we derive the parent answer from the initial dim states.
  //
  // Runs whenever the server-hydrated initial state changes (e.g. after
  // a policy import drops drafts and the row's getRowState query
  // refetches). The selectedOptionKey===null guard prevents overwriting
  // a user pick with stale server data on a later re-fetch.
  useEffect(() => {
    if (selectedOptionKey !== null) return
    setParentRationale(initial.rationale ?? '')
    if (blueprint.policyScope?.inclusion === 'extended' && (
      (blueprint.includedInPolicy === false && initial.attestationAnswer === 'YES') ||
      (!initial.attestationAnswer && !initial.optionKey &&
        Object.keys(initial.dimensionStates ?? {}).length === 0)
    )) {
      setSelectedOptionKey(POLICY_EXCLUSION_OPTION_KEY)
      return
    }
    if (initial.optionKey === POLICY_EXCLUSION_OPTION_KEY && blueprint.policyScope?.inclusion === 'extended') {
      setSelectedOptionKey(initial.optionKey)
      setCommittedKey(initial.optionKey)
      onAnswered?.(blueprint.id, 'UNKNOWN', initial.optionKey, 'hydration')
      return
    }
    if (hasDimensions) {
      const initialStates = initial.dimensionStates ?? {}
      const hasAnyState = Object.keys(initialStates).length > 0
      // Sync local dimStates from server-hydrated state. Without this
      // the lazy useState initializer captures whatever initial was on
      // first render (often empty before getRowState resolves), and the
      // dim pills render unselected even when the server has answers
      // for them — making the sidebar count and the row UI disagree.
      if (hasAnyState) setDimStates(initialStates)
      const derived = deriveDimAnswer(initialStates)
      if (derived) {
        const optKey = derived.toLowerCase()
        setSelectedOptionKey(optKey)
        setCommittedKey(optKey)
        onAnswered?.(blueprint.id, derived, optKey, 'hydration')
      }
      return
    }
    // Prefer the draft's raw optionKey when the server surfaces it —
    // required for bucket-style questions (RTO/RPO/SLA/etc) where
    // multiple option keys collapse to the same attestationAnswer and
    // a reverse-map from attestationAnswer would lose the user's
    // specific pick. Falls back to the attestationAnswer→option_key
    // lookup for legacy responses without optionKey populated.
    if (initial.optionKey && blueprint.question.options) {
      const match = blueprint.question.options.find(
        (o) => o.key === initial.optionKey
      )
      if (match) {
        setSelectedOptionKey(match.key)
        setCommittedKey(match.key)
        return
      }
    }
    if (initial.attestationAnswer && blueprint.question.options) {
      const fallback = initial.attestationAnswer.toLowerCase()
      const match = blueprint.question.options.find(
        (o) => o.key === fallback || (o.key === 'na' && fallback === 'na')
      )
      if (match) {
        setSelectedOptionKey(match.key)
        setCommittedKey(match.key)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    blueprint.id,
    initial.attestationAnswer,
    initial.optionKey,
    initial.dimensionStates,
    blueprint.includedInPolicy,
  ])

  const submitMutation = useMutation(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    trpc.readinessQuestionnaire.submitAnswer.mutationOptions() as any
  )

  const exceptionRequests = useQuery({
    ...trpc.readinessQuestionnaire.getExceptionRequests.queryOptions({ programId, blueprintId: blueprint.id }),
    enabled: hasDimensions || practiceQuestion,
  })
  const exceptionMutation = useMutation(trpc.readinessQuestionnaire.requestException.mutationOptions())
  const dimensionFollowups = useQuery({
    ...trpc.readinessQuestionnaire.getDimensionFollowups.queryOptions({ programId, blueprintId: blueprint.id }),
    enabled: hasDimensions || practiceQuestion || Boolean(selectedOptionKey && ['PARTIAL', 'NO'].includes(blueprint.question.options?.find((option) => option.key === selectedOptionKey)?.verdict ?? inferAttestationAnswer(selectedOptionKey))),
  })
  const detailsMutation = useMutation(trpc.readinessQuestionnaire.saveDimensionDetails.mutationOptions())
  const [exceptionErrors, setExceptionErrors] = useState<Record<string, string | null>>({})
  const saveException = async (key: string, note: string) => {
    const next: DimensionStateMap = { ...dimStates, [key]: { state: 'accept', note } }
    setExceptionErrors((errors) => ({ ...errors, [key]: null }))
    draftSaveCountRef.current += 1
    onDraftSavingChange?.(blueprint.id, true)
    try {
      await exceptionMutation.mutateAsync({ programId, blueprintId: blueprint.id, dimensionKey: key, optionKey: hasDimensions ? dimAnswerOptionKey(next) : 'no', dimensionStates: next })
      await Promise.all([
        exceptionRequests.refetch(),
        queryClient.invalidateQueries({ queryKey: trpc.readinessQuestionnaire.getRowState.queryKey({ programId, blueprintId: blueprint.id }) }),
        queryClient.invalidateQueries({ queryKey: trpc.readinessQuestionnaire.getProgress.queryKey() }),
      ])
      const derived = hasDimensions ? deriveDimAnswer(next) : 'NO'
      const optionKey = hasDimensions ? dimAnswerOptionKey(next) : 'no'
      setSelectedOptionKey(optionKey)
      if (derived) onAnswered?.(blueprint.id, derived, optionKey)
    } catch (err) {
      setExceptionErrors((errors) => ({ ...errors, [key]: err instanceof Error ? err.message : 'Exception request could not be saved' }))
    } finally {
      draftSaveCountRef.current = Math.max(0, draftSaveCountRef.current - 1)
      if (draftSaveCountRef.current === 0) onDraftSavingChange?.(blueprint.id, false)
    }
  }

  const saveDetails = async (key: string, work: QuestionnaireRemediationPlanDTO = {}): Promise<boolean> => {
    setExceptionErrors((errors) => ({ ...errors, [key]: null }))
    draftSaveCountRef.current += 1
    onDraftSavingChange?.(blueprint.id, true)
    try {
      await detailsMutation.mutateAsync({ programId, blueprintId: blueprint.id, dimensionKey: key,
        optionKey: hasDimensions ? dimAnswerOptionKey(dimStates) : practiceQuestion ? 'no' : selectedOptionKey!,
        dimensionStates: hasDimensions || practiceQuestion ? dimStates : undefined,
        rationale: hasDimensions ? undefined : parentRationale, ...work })
      await Promise.all([
        dimensionFollowups.refetch(),
        queryClient.invalidateQueries({ queryKey: trpc.readinessQuestionnaire.getRowState.queryKey({ programId, blueprintId: blueprint.id }) }),
        queryClient.invalidateQueries({ queryKey: trpc.readinessQuestionnaire.getProgress.queryKey() }),
        queryClient.invalidateQueries({ queryKey: trpc.complianceProgram.getPolicyQuestionnaireBlueprints.queryKey() }),
      ])
      const answerOptionKey = hasDimensions ? dimAnswerOptionKey(dimStates) : selectedOptionKey!
      const derived = hasDimensions ? deriveDimAnswer(dimStates) : options.find((option) => option.key === answerOptionKey)?.verdict ?? inferAttestationAnswer(answerOptionKey)
      setSelectedOptionKey(answerOptionKey)
      if (derived) onAnswered?.(blueprint.id, derived, answerOptionKey)
      return true
    } catch (err) {
      setExceptionErrors((errors) => ({ ...errors, [key]: err instanceof Error ? err.message : 'Details could not be saved' }))
      return false
    } finally {
      draftSaveCountRef.current = Math.max(0, draftSaveCountRef.current - 1)
      if (draftSaveCountRef.current === 0) onDraftSavingChange?.(blueprint.id, false)
    }
  }

  const persistDraft = async (
    optionKey: string,
    nextStates: DimensionStateMap,
    rationale?: string
  ) => {
    draftSaveCountRef.current += 1
    onDraftSavingChange?.(blueprint.id, true)
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (submitMutation as any).mutateAsync({
        programId,
        blueprintId: blueprint.id,
        optionKey,
        dimensionStates: nextStates,
        rationale,
      })
      // Invalidate the section-progress query so the sidebar counts +
      // wizard "X unanswered" inline hint reflect this answer immediately.
      // Use both the partial procedure key (covers all variants) AND
      // explicitly refetch active queries so the UI updates without a
      // staleTime delay.
      await queryClient.invalidateQueries({
        queryKey: trpc.readinessQuestionnaire.getProgress.queryKey(),
        refetchType: 'active',
      })
      await queryClient.invalidateQueries({
        queryKey: trpc.readinessQuestionnaire.getRowState.queryKey({
          programId,
          blueprintId: blueprint.id,
        }),
        refetchType: 'active',
      })
    } finally {
      draftSaveCountRef.current = Math.max(0, draftSaveCountRef.current - 1)
      if (draftSaveCountRef.current === 0) {
        onDraftSavingChange?.(blueprint.id, false)
      }
    }
  }

  // RFC-172 Stage 7 — derive the parent attestationAnswer from per-dim
  // states for dim'd blueprints. Single source of truth for the row's
  // answer. Section completion gating depends on every required dim
  // being touched (yes / no→r/c/a) — partial completion returns null
  // so the wizard's onComplete predicate keeps the section open.
  //   every required dim = yes → YES
  //   every required dim touched, mix → PARTIAL
  //   every required dim touched, zero yes → NO
  //   any required dim untouched → null (incomplete; section stays open)
  const deriveDimAnswer = (
    states: DimensionStateMap
  ): AttestationAnswerValue | null => {
    const dims = blueprint.dimensions ?? []
    const required = dims.filter((d) => d.required)
    if (required.length === 0) return null
    let yesCount = 0
    for (const d of required) {
      const entry = states[d.key]
      if (!entry && practiceQuestion) { yesCount++; continue }
      if (!entry) return null // any untouched required dim → incomplete
      if (entry.state === 'yes') yesCount++
    }
    if (yesCount === required.length) return 'YES'
    if (yesCount === 0) return 'NO'
    return 'PARTIAL'
  }

  const dimAnswerOptionKey = (states: DimensionStateMap): string => {
    const ans = deriveDimAnswer(states)
    if (ans === 'YES') return 'yes'
    if (ans === 'PARTIAL') return 'partial'
    if (ans === 'NO') return 'no'
    // No required dim has been touched yet — pick a placeholder optionKey
    // so the draft persists. Using 'partial' as the safe middle since the
    // user has expressed something (clicked into the row) but hasn't
    // committed the full claim yet.
    return 'partial'
  }

  // Auto-default the per-dim states when the parent answer changes.
  // Yes parent → all dims = yes (panel hides); No parent → required
  // dims default to remediate-no-note; Partial parent → start blank
  // so the user picks per dim.
  const computeAutoStates = (optionKey: string): DimensionStateMap => {
    const dims = blueprint.dimensions
    if (!dims || dims.length === 0) return {}
    if (optionKey === 'yes') {
      return Object.fromEntries(
        dims.filter((d) => !practiceQuestion || d.required).map((d) => [d.key, { state: 'yes' as const }])
      )
    }
    if (optionKey === 'no') {
      return Object.fromEntries(
        dims
          .filter((d) => d.required)
          .map((d) => [d.key, { state: 'remediate' as const }])
      )
    }
    // partial / unknown / na — leave dims blank for the user to pick.
    return {}
  }

  const handleSelect = async (optionKey: string) => {
    if (!hasDimensions && selectedOptionKey === optionKey) return
    setSelectedOptionKey(optionKey)
    setParentRationale('')
    setParentSaveError(null)
    setParentDetailsSaved(false)
    // Custom policy wording is saved together with the selected option.
    if (blueprint.question.options?.find((option) => option.key === optionKey)?.policyTextFromRationale) return
    setPendingKey(optionKey)
    const nextStates = computeAutoStates(optionKey)
    setDimStates(nextStates)
    try {
      await persistDraft(optionKey, nextStates)
      setCommittedKey(optionKey)
      const verdict = blueprint.question.options?.find(
        (o) => o.key === optionKey
      )?.verdict
      const normalized = verdict ?? inferAttestationAnswer(optionKey)
      onAnswered?.(blueprint.id, normalized, optionKey)
    } finally {
      setPendingKey(null)
    }
  }

  // Per-dim pill click — sole input for dim'd blueprints. Persists
  // the per-dim state map and the derived parent optionKey on every
  // change. The wizard's `value` shape is updated via onAnswered with
  // the derived YES / PARTIAL / NO so completion gating still works.
  const handleDimState = async (
    dimensionKey: string,
    state: DimensionState | null
  ) => {
    const cur = dimStates[dimensionKey]
    const next: DimensionStateMap = {
      ...dimStates,
      ...(state ? { [dimensionKey]: cur?.state === state ? cur : { state } } : {}),
    }
    if (!state) delete next[dimensionKey]
    setDimStates(next)
    if (state === 'accept') return // Deliberate save after entering a rationale.
    if (hasDimensions) {
      const derivedKey = dimAnswerOptionKey(next)
      try {
        await persistDraft(derivedKey, next)
        setSelectedOptionKey(derivedKey)
        setCommittedKey(derivedKey)
        const derivedAnswer = deriveDimAnswer(next)
        if (derivedAnswer) onAnswered?.(blueprint.id, derivedAnswer, derivedKey)
      } catch {
        // Auto-save failure is non-fatal — local state reflects intent.
      }
      return
    }
    if (practiceQuestion) {
      const optionKey = state === 'yes' ? 'yes' : 'no'
      setSelectedOptionKey(optionKey)
      try {
        await persistDraft(optionKey, next, parentRationale)
        onAnswered?.(blueprint.id, state === 'yes' ? 'YES' : 'NO', optionKey)
      }
      catch (error) { setExceptionErrors((errors) => ({ ...errors, [dimensionKey]: error instanceof Error ? error.message : 'Details could not be saved' })) }
      return
    }
    // Legacy path: parent has options + dims layered on PARTIAL/NO.
    if (!selectedOptionKey || selectedOptionKey === 'yes') return
    try {
      await persistDraft(selectedOptionKey, next)
    } catch {
      // Auto-save failure is non-fatal — local state reflects intent.
    }
  }

  const options = blueprint.question.options ?? []

  // Rows are always fully expanded — collapse/expand was removed per
  // onboarding feedback. Folding a row hid the pills + tips the user
  // needs to review and switch answers; the chevron added a click that
  // bought nothing. "Answered" still gates the green check + badge.
  const isAnswered = practiceQuestion || selectedOptionKey === POLICY_EXCLUSION_OPTION_KEY || (
    hasDimensions
      ? deriveDimAnswer(dimStates) !== null
      : selectedOptionKey !== null
  )

  // "Ask Vera" — seed the assistant with this question's context.
  // Inside onboarding the global assistant panel is hidden, so the wizard
  // exposes its own chat rail via OnboardingAssistantContext; prefer that
  // when present. Elsewhere (e.g. the PolicyQuestionnaireDialog) the
  // global panel IS mounted, so fall back to useAskAi.
  const openAskAi = useAskAi()
  const onboardingAssistant = useOnboardingAssistant()
  const askThreadName =
    questionNumber != null
      ? `Readiness Q${questionNumber}`
      : 'Readiness question'
  const askPageData = {
    programId,
    blueprintId: blueprint.id,
    controlRef: blueprint.controlRef ?? undefined,
    slotKey: blueprint.slotKey ?? undefined,
  }
  const askVeraAboutQuestion = () => {
    const text = `I'm filling out the readiness questionnaire. Help me understand and answer this question: "${blueprint.question.promptTemplate}"`
    if (onboardingAssistant) {
      onboardingAssistant.askVera({
        text,
        data: { ...askPageData, questionNumber },
      })
      return
    }
    void openAskAi({
      text,
      page: 'onboarding-questionnaire',
      pageSummary: `Readiness questionnaire: ${blueprint.question.promptTemplate}`,
      pageData: askPageData,
      activityContext: { blueprintId: blueprint.id, questionNumber },
      threadName: askThreadName,
      sendMessage: true,
    })
  }
  const askVeraAboutDimension = (dim: { key: string; label: string }) => {
    const text = `In the readiness questionnaire question "${blueprint.question.promptTemplate}", help me with this part: "${dim.label}". What does it mean and how should I answer it?`
    if (onboardingAssistant) {
      onboardingAssistant.askVera({
        text,
        data: { ...askPageData, dimensionKey: dim.key },
      })
      return
    }
    void openAskAi({
      text,
      page: 'onboarding-questionnaire',
      pageSummary: `Readiness questionnaire: ${blueprint.question.promptTemplate}`,
      pageData: { ...askPageData, dimensionKey: dim.key },
      activityContext: { blueprintId: blueprint.id, dimensionKey: dim.key },
      threadName: askThreadName,
      sendMessage: true,
    })
  }

  const renderScannerEvidenceLink = (
    run: NonNullable<RowInitialState['scannerEvidence']>['runs'][number],
    className: string
  ) => {
    const label = (
      <>
        {run.provider} · {new Date(run.finishedAt).toLocaleString()} · View
        evidence
        <ExternalLink className='h-3 w-3' />
      </>
    )
    if (run.source === 'COMPLIANCE_SCAN') {
      return (
        <button
          key={`${run.id}:${run.checkId ?? ''}`}
          type='button'
          onClick={() => setSelectedPostureRun(run)}
          className={className}
        >
          {label}
        </button>
      )
    }
    return (
      <a
        key={run.id}
        href={
          workspaceId && run.statusId
            ? `/workspaces/${workspaceId}/tests/${run.statusId}?apiRunId=${run.id}`
            : undefined
        }
        className={className}
      >
        {label}
      </a>
    )
  }

  const scannerAnswer = initial.scannerEvidence?.answer
  let scannerPanel = {
    label: 'Supporting scan evidence',
    border: 'border-border',
    background: 'bg-surface-muted',
    text: 'text-text-body',
    icon: ShieldCheck,
  }
  if (scannerAnswer === 'NO') {
    scannerPanel = {
      label: 'Scan detected a gap',
      border: 'border-[#E7C2BC]',
      background: 'bg-status-red-xsubtle',
      text: 'text-[#7D2F24]',
      icon: AlertTriangle,
    }
  } else if (scannerAnswer === 'NA') {
    scannerPanel = {
      label: 'Not applicable based on scan',
      border: 'border-border',
      background: 'bg-surface-muted',
      text: 'text-text-body',
      icon: ShieldCheck,
    }
  } else if (scannerAnswer === 'YES') {
    const providers = [
      ...new Set(
        initial.scannerEvidence?.runs.map((run) => run.provider) ?? []
      ),
    ]
    scannerPanel = {
      label: `Verified by ${providers.join(', ')} scan`,
      border: 'border-[#BFE2CF]',
      background: 'bg-[#F0F8F4]',
      text: 'text-[#146C43]',
      icon: ShieldCheck,
    }
  }
  const ScannerPanelIcon = scannerPanel.icon

  return (
    <div
      className={cn(
        'rounded-md border transition-colors',
        // Soft shadow on unanswered rows signals "needs your input";
        // answered rows flatten to read as "done."
        isAnswered
          ? 'border-border bg-card'
          : 'border-border bg-card shadow-[0_1px_2px_rgba(13,13,13,0.04)]',
        'p-5'
      )}
    >
      {blueprint.policyScope?.inclusion === 'extended' && (
        <div className='border-border bg-surface-subtle -mx-5 -mt-5 mb-4 rounded-t-md border-b px-5 py-3'>
          <Badge variant='outline'>Extended · Optional</Badge>
          <p className='text-foreground mt-1.5 text-xs font-medium'>
            Not required for your current program. Excluded from the policy by default.
          </p>
        </div>
      )}
      <div className='flex items-start justify-between gap-3'>
        {/* Question prompt. Number prefix for channel reference; imported-
            policy provenance badge kept inline. Answered-check and the
            Attested / Continuous status pills were removed as noise. */}
        <div className='font-heading text-foreground min-w-0 flex-1 text-base leading-snug font-semibold'>
          {questionNumber != null && (
            <span className='font-body text-text-placeholder mr-1.5 font-normal tabular-nums'>
              {questionNumber}.
            </span>
          )}
          {practiceQuestion && blueprint.itemTitle ? blueprint.itemTitle : blueprint.question.promptTemplate}
          {blueprint.policyScope && blueprint.policyScope.inclusion !== 'extended' && (
            <Badge variant='outline' status={blueprint.policyScope.inclusion === 'context' ? 'default' : 'blue'} className='ml-2 align-middle'>
              {blueprint.policyScope.inclusion === 'context' ? 'Core' : blueprint.policyScope.inclusion === 'scope' ? 'Scope' : 'Core'}
            </Badge>
          )}
          {initial.importMeta && (
            <span className='ml-2 inline-flex items-center align-middle'>
              <ImportedBadge
                tier={
                  initial.importMeta.source === 'imported-low-confidence'
                    ? 'review'
                    : initial.importMeta.overallConfidence >= 0.9
                      ? 'high'
                      : 'review'
                }
                importMeta={initial.importMeta}
              />
            </span>
          )}
          {!practiceQuestion &&
            !initial.importMeta &&
            (initial.sourceLabel === 'Prefilled default' ||
              initial.sourceLabel === 'Attested') && (
              <span className='font-body text-text-subtle ml-2 inline-flex items-center rounded-md bg-[#F1F3F4] px-2 py-0.5 align-middle text-[10px] font-medium tracking-[0.04em] uppercase'>
                {initial.sourceLabel}
              </span>
            )}
        </div>
        <AskVeraButton onClick={askVeraAboutQuestion} className='mt-0.5' />
      </div>

      {practiceQuestion && practiceInScope && (
        <div className='mt-3 space-y-2'>
          {blueprint.policyTextWhenConfirmed && <p className='text-text-body text-sm leading-relaxed'>{blueprint.policyTextWhenConfirmed}</p>}
          <p className='text-text-subtle text-xs leading-relaxed'>
            {selectedOptionKey === POLICY_EXCLUSION_OPTION_KEY ? 'Optional requirement. Include it if you want this commitment in your policy.' : 'Included in your policy. Linked tests check that it is in place.'}
          </p>
        </div>
      )}

      {blueprint.policyScope?.inclusion === 'extended' && (
        <p className='font-body text-text-subtle mt-2 text-xs'>
          Include this practice only if you want to commit to it in your policy.
        </p>
      )}

      {initial.scannerEvidence &&
        !initial.conflict &&
        initial.scannerEvidence.runs.length > 0 && (
          <div
            className={cn(
              'mt-4 rounded-md border px-3 py-2.5',
              scannerPanel.border,
              scannerPanel.background
            )}
          >
            <div className='flex items-start gap-2'>
              <ScannerPanelIcon
                className={cn('mt-0.5 h-4 w-4 shrink-0', scannerPanel.text)}
              />
              <div className='min-w-0 flex-1'>
                <p
                  className={cn(
                    'font-body text-xs font-medium',
                    scannerPanel.text
                  )}
                >
                  {scannerPanel.label}
                </p>
                <div className='mt-1 flex flex-wrap gap-x-3 gap-y-1'>
                  {initial.scannerEvidence.runs.map((run) =>
                    renderScannerEvidenceLink(
                      run,
                      'font-body inline-flex items-center gap-1 text-[11px] text-[#3159C5] hover:underline'
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      {initial.scannerEvidence?.supportingRuns &&
        initial.scannerEvidence.supportingRuns.length > 0 && (
          <div className='border-border mt-3 rounded-md border bg-surface-muted px-3 py-2.5'>
            <div className='flex items-start gap-2'>
              <ShieldCheck className='text-text-body mt-0.5 h-4 w-4 shrink-0' />
              <div className='min-w-0 flex-1'>
                <p className='font-body text-text-body text-xs font-medium'>
                  Supporting scan evidence
                </p>
                <div className='mt-1 flex flex-wrap gap-x-3 gap-y-1'>
                  {initial.scannerEvidence.supportingRuns.map((run) =>
                    renderScannerEvidenceLink(
                      run,
                      'font-body inline-flex items-center gap-1 text-[11px] text-[#3159C5] hover:underline'
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      {initial.conflict && (
        <div className='mt-3 rounded-md border border-[#E7C2BC] bg-status-red-xsubtle px-3 py-2.5'>
          <div className='flex items-start gap-2'>
            <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-[#A33A2B]' />
            <div className='font-body min-w-0 text-xs text-[#7D2F24]'>
              <p className='font-medium'>
                {practiceQuestion ? `The policy requires this practice; the latest scan shows ${formatAnswer(initial.conflict.scannerAnswer)}. Review the linked test and remediate if needed.` : formatScannerConflict(initial.conflict)}
              </p>
              {initial.conflict.policySlug && (
                <a
                  href={
                    workspaceId
                      ? `/workspaces/${workspaceId}/policies/${initial.conflict.policySlug}`
                      : undefined
                  }
                  className='mt-1 inline-flex items-center gap-1 text-[11px] text-[#8A3D31] hover:underline'
                >
                  Policy source:{' '}
                  {initial.conflict.policyTitle ?? initial.conflict.policySlug}
                  <ExternalLink className='h-3 w-3' />
                </a>
              )}
              {initial.conflict.citations?.map((citation) => (
                <p
                  key={citation}
                  className='mt-1 text-[11px] leading-relaxed text-[#8A5148]'
                >
                  “{citation}”
                </p>
              ))}
              {initial.scannerEvidence?.runs.map((run) =>
                renderScannerEvidenceLink(
                  run,
                  'mt-1 inline-flex items-center gap-1 text-[11px] text-[#8A3D31] hover:underline'
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* Parent answer — radio rows (same pattern as sub-questions).
          Rendered only for binary blueprints; dim'd blueprints derive
          the parent answer from per-dim states. */}
      {practiceQuestion && !practiceInScope && <p className='text-text-subtle mt-4 text-sm'>Not included under the current program scope. Update scope in Program settings if this practice applies.</p>}
      {!hasDimensions && practiceQuestion && practiceInScope && (
        <div className='mt-4'>
          {blueprint.policyScope?.inclusion === 'extended' && (
            <button type='button' disabled={Boolean(pendingKey)} className='border-border mb-3 rounded-md border px-3 py-2 text-sm'
              onClick={() => handleSelect(selectedOptionKey === POLICY_EXCLUSION_OPTION_KEY ? 'yes' : POLICY_EXCLUSION_OPTION_KEY)}>
              {selectedOptionKey === POLICY_EXCLUSION_OPTION_KEY ? 'Include in policy' : 'Do not include in policy'}
            </button>
          )}
          {selectedOptionKey !== POLICY_EXCLUSION_OPTION_KEY && <DimensionRow
            programId={programId} blueprintId={blueprint.id} policyRequirement
            dimension={{ key: 'aggregate', label: 'Policy requirement', policyFragment: blueprint.policyTextWhenConfirmed ?? blueprint.question.promptTemplate, required: true }}
            entry={dimStates.aggregate ?? (initial.attestationAnswer === 'NO' || initial.attestationAnswer === 'PARTIAL' ? { state: 'remediate', note: parentRationale } : undefined)}
            exception={exceptionRequests.data?.aggregate} followup={dimensionFollowups.data?.aggregate}
            loadingFollowups={dimensionFollowups.isLoading || dimensionFollowups.isError}
            workspaceId={workspaceId} savingException={exceptionMutation.isPending || detailsMutation.isPending || Boolean(pendingKey)}
            exceptionError={exceptionErrors.aggregate ?? null}
            onDetailChange={(key, updates) => { setDimStates((states) => ({ ...states, [key]: { ...(states[key] ?? { state: 'remediate' }), ...updates } })); if (updates.note !== undefined) setParentRationale(updates.note) }}
            onSaveDetails={saveDetails} onSaveException={saveException} onState={handleDimState}
          />}
        </div>
      )}

      {!hasDimensions && !practiceQuestion && (
        <div
          className='-mx-2.5 mt-6 space-y-1.5'
          role='radiogroup'
          aria-label={blueprint.question.promptTemplate}
        >
          {options.map((option) => {
            const active = selectedOptionKey === option.key
            const isSuggested =
              initial.vendorHint?.optionKey === option.key && !active
            const verdict = option.verdict ?? inferAttestationAnswer(option.key)
            return (
              <div key={option.key}>
                <button
                  type='button'
                  role='radio'
                  aria-checked={active}
                  disabled={Boolean(pendingKey)}
                  onClick={() => handleSelect(option.key)}
                  className={cn(
                    'flex w-full items-start gap-3 rounded-md px-2.5 py-2.5 text-left transition-colors',
                    active ? 'bg-cobalt-selected-faint' : 'hover:bg-background',
                    pendingKey && 'opacity-60'
                  )}
                >
                  <span
                    className={cn(
                      'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                      active ? 'border-ring' : 'border-[#C4C4C2]'
                    )}
                  >
                    {active && (
                      <span className='bg-primary h-1.5 w-1.5 rounded-full' />
                    )}
                  </span>
                  <span className='min-w-0 flex-1'>
                    <span className={cn('font-body block text-sm font-medium leading-relaxed', active ? 'text-cobalt-strong' : 'text-text-body')}>
                      {option.label}
                      {option.key === POLICY_EXCLUSION_OPTION_KEY && (
                        <span className='text-text-subtle ml-2 text-xs font-normal'>Default</span>
                      )}
                    </span>
                    <span className={cn('font-body mt-0.5 block text-xs leading-relaxed', active ? 'text-text-body' : 'text-text-subtle')}>
                      {option.hint}
                      {option.recommended && !['yes', 'partial', 'no', 'na', POLICY_EXCLUSION_OPTION_KEY].includes(option.key) && (
                        <span className='ml-1 font-medium text-cobalt-strong'>· recommended</span>
                      )}
                      {isSuggested && initial.vendorHint && (
                        <span className='ml-1 text-cobalt-strong'>
                          · suggested ({initial.vendorHint.reason})
                        </span>
                      )}
                    </span>
                  </span>
                </button>
                {active && blueprint.question.answerMode !== 'fact' && (verdict === 'PARTIAL' || verdict === 'NO') && (
                  <DimensionRow programId={programId} blueprintId={blueprint.id} followupOnly gapDetailsLabel={verdict === 'PARTIAL' ? 'What is already covered, and what is missing?' : 'What is missing?'}
                    dimension={{ key: 'aggregate', label: blueprint.question.promptTemplate, policyFragment: '', required: true }}
                    entry={{ state: 'remediate', note: parentRationale }}
                    followup={dimensionFollowups.data?.aggregate}
                    loadingFollowups={dimensionFollowups.isLoading || dimensionFollowups.isError}
                    workspaceId={workspaceId} savingException={detailsMutation.isPending || Boolean(pendingKey)}
                    exceptionError={exceptionErrors.aggregate ?? (dimensionFollowups.isError ? 'Could not load remediation plans. Please retry.' : null)}
                    onDetailChange={(_key, updates) => { if (updates.note !== undefined) setParentRationale(updates.note); setExceptionErrors((errors) => ({ ...errors, aggregate: null })) }}
                    onSaveDetails={saveDetails} onSaveException={saveException} onState={handleDimState}
                  />
                )}
                {active && option.key !== POLICY_EXCLUSION_OPTION_KEY && (option.policyTextFromRationale || verdict === 'NA' || verdict === 'UNKNOWN') && (
                  <div className='border-border bg-muted/30 mx-2.5 mb-3 mt-3 space-y-3 rounded-md border p-3'>
                    <label className='text-text-secondary block text-xs'>
                      {option.policyTextFromRationale ? 'Policy wording (required)' : verdict === 'NA' ? 'Why does this practice not apply? (required)' : 'What needs to be confirmed?'}
                      <textarea aria-label={`${option.policyTextFromRationale ? 'Policy wording' : verdict === 'NA' ? 'Applicability rationale' : 'Uncertainty details'}: ${blueprint.question.promptTemplate}`}
                        value={parentRationale} maxLength={4000} rows={3} disabled={Boolean(pendingKey)}
                        onChange={(event) => { setParentRationale(event.target.value); setParentSaveError(null); setParentDetailsSaved(false) }}
                        className='border-border bg-background text-text-body mt-1 block w-full rounded-md border p-2 text-sm'
                        placeholder={option.policyTextFromRationale ? option.hint : verdict === 'NA' ? 'Explain the scope or company circumstances that make this practice irrelevant.' : 'Describe the information you need and who can confirm it.'} />
                    </label>
                    {verdict === 'NA' && <p className='text-text-subtle text-xs'>This records your applicability explanation. It does not change framework scope or approve an exception to an applicable requirement.</p>}
                    {parentSaveError && <p role='alert' className='text-destructive text-xs'>{parentSaveError}</p>}
                    <div className='flex items-center gap-3'>
                      <button type='button' disabled={Boolean(pendingKey) || ((option.policyTextFromRationale || verdict === 'NA') && !parentRationale.trim())}
                        className='border-border rounded-md border px-3 py-1.5 text-sm disabled:opacity-50'
                        onClick={async () => {
                          setPendingKey(option.key); setParentSaveError(null)
                          try {
                            await persistDraft(option.key, {}, parentRationale)
                            setParentDetailsSaved(true)
                            if (option.policyTextFromRationale) {
                              setCommittedKey(option.key)
                              onAnswered?.(blueprint.id, verdict, option.key)
                            }
                          }
                          catch (error) { setParentSaveError(error instanceof Error ? error.message : 'Details could not be saved') }
                          finally { setPendingKey(null) }
                        }}>{pendingKey ? 'Saving…' : option.policyTextFromRationale ? 'Save policy wording' : 'Save explanation'}</button>
                      {parentDetailsSaved && <span role='status' className='text-text-subtle text-xs'>Saved</span>}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* RFC-172 Stage 7 — dim'd blueprints: per-dim panel, no parent
          pill bar (parent answer is derived from per-dim states). */}
      {hasDimensions && (!practiceQuestion || practiceInScope) && (
        <>
          {blueprint.policyScope?.inclusion === 'extended' && (
            <button
              type='button'
              aria-pressed={selectedOptionKey === POLICY_EXCLUSION_OPTION_KEY}
              disabled={Boolean(pendingKey) || exceptionMutation.isPending || detailsMutation.isPending}
              onClick={() => handleSelect(POLICY_EXCLUSION_OPTION_KEY)}
              className={cn(
                'border-border mt-4 rounded-md border px-3 py-2 text-sm',
                selectedOptionKey === POLICY_EXCLUSION_OPTION_KEY &&
                  'bg-cobalt-selected-faint text-cobalt-strong'
              )}
            >
              {selectedOptionKey === POLICY_EXCLUSION_OPTION_KEY ? 'Not included in policy' : 'Do not include in policy'}
            </button>
          )}
          {(!practiceQuestion || selectedOptionKey !== POLICY_EXCLUSION_OPTION_KEY) && <DimensionsPanel
            policyRequirement={practiceQuestion}
            programId={programId} blueprintId={blueprint.id}
            dimensions={blueprint.dimensions!}
            exceptions={exceptionRequests.data}
            followups={dimensionFollowups.data}
            loadingFollowups={dimensionFollowups.isLoading || dimensionFollowups.isError}
            workspaceId={workspaceId}
            savingException={exceptionMutation.isPending || detailsMutation.isPending}
            exceptionErrors={exceptionErrors}
            exceptionLoadError={exceptionRequests.isError ? 'Could not load exception decisions. Please retry.' : null}
            followupLoadError={dimensionFollowups.isError ? 'Could not load remediation plans. Please retry before editing them.' : null}
            onDetailChange={(key, updates) => {
              setDimStates((states) => ({ ...states, [key]: { ...states[key], ...updates } }))
              setExceptionErrors((errors) => ({ ...errors, [key]: null }))
            }}
            onSaveException={saveException}
            onSaveDetails={saveDetails}
            states={dimStates}
            optionalCommitment={blueprint.policyScope?.inclusion === 'extended'}
            onState={handleDimState}
            questionNumber={questionNumber}
            onAskDimension={askVeraAboutDimension}
          />}
        </>
      )}

      <ProviderCheckDetailSheet
        open={selectedPostureRun !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedPostureRun(null)
        }}
        check={
          selectedPostureRun?.checkId
            ? {
                id: selectedPostureRun.checkId,
                name: selectedPostureRun.checkId,
                description: 'Findings captured by this posture scan.',
                lastRunId: selectedPostureRun.id,
              }
            : null
        }
        programId={programId}
        provider={selectedPostureRun?.provider ?? ''}
        connectionId={selectedPostureRun?.connectionId ?? null}
        runId={selectedPostureRun?.id}
      />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// RFC-172 Stage 7 — Dimensions panel.
//
// Renders the per-axis checklist that used to live inside long
// descriptive option labels (e.g. "Yes, ticketed + approved + tested
// + released"). Two framings depending on the user's choice:
//   - Partial → "Which parts apply?"
//   - No      → "Anything you do have? (optional)"
//
// Required dimensions show a small "required" tag so the user can see
// what's expected for a full YES.
// ─────────────────────────────────────────────────────────────────

function DimensionsPanel({
  policyRequirement = false,
  programId, blueprintId,
  exceptions, followups, loadingFollowups, workspaceId, savingException, exceptionErrors, exceptionLoadError, followupLoadError, onDetailChange, onSaveException, onSaveDetails,
  dimensions,
  states,
  optionalCommitment = false,
  onState,
  questionNumber,
  onAskDimension,
}: {
  policyRequirement?: boolean
  programId: string
  blueprintId: string
  exceptions?: Record<string, PolicyExceptionRequestDTO>
  followups?: Record<string, PolicyQuestionDimensionFollowupDTO>
  loadingFollowups: boolean
  workspaceId?: string
  savingException: boolean
  exceptionErrors: Record<string, string | null>
  exceptionLoadError: string | null
  followupLoadError: string | null
  onDetailChange: (key: string, updates: Partial<DimensionStateEntry>) => void
  onSaveException: (key: string, note: string) => Promise<void>
  onSaveDetails: (key: string, work?: QuestionnaireRemediationPlanDTO) => Promise<boolean>
  dimensions: NonNullable<QuestionnaireBlueprintForRow['dimensions']>
  states: DimensionStateMap
  optionalCommitment?: boolean
  onState: (key: string, state: DimensionState | null) => void
  /** Parent question's ordinal — sub-questions render as "N.1", "N.2"… */
  questionNumber?: number
  /** Opens Vera seeded with the given sub-question's context. */
  onAskDimension?: (dim: { key: string; label: string }) => void
}) {
  // Flat sub-questions separated by generous spacing — no card boxes.
  // The selected radio + highlighted row carry the answered state, so an
  // enclosing card adds weight without communicating hierarchy.
  return (
    <div className={policyRequirement ? 'mt-5 space-y-5' : 'mt-6 space-y-9'}>
      {exceptionLoadError && <p role='alert' className='text-destructive text-xs'>{exceptionLoadError}</p>}
      {followupLoadError && <p role='alert' className='text-destructive text-xs'>{followupLoadError}</p>}
      {dimensions.map((d, i) => (
        <DimensionRow
          policyRequirement={policyRequirement}
          programId={programId} blueprintId={blueprintId}
          key={d.key}
          dimension={d}
          exception={exceptions?.[d.key]}
          followup={followups?.[d.key]}
          loadingFollowups={loadingFollowups}
          workspaceId={workspaceId}
          savingException={savingException}
          exceptionError={exceptionErrors[d.key] ?? null}
          onDetailChange={onDetailChange}
          onSaveException={onSaveException}
          onSaveDetails={onSaveDetails}
          entry={states[d.key]}
          optionalCommitment={optionalCommitment}
          onState={onState}
          number={
            questionNumber != null ? `${questionNumber}.${i + 1}` : undefined
          }
          onAsk={
            onAskDimension
              ? () => onAskDimension({ key: d.key, label: d.label })
              : undefined
          }
        />
      ))}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// RFC-172 Stage 7 — DimensionRow.
//
// One radio row per option — circle + label + meaning, the whole row
// clickable. Single-select across the four dispositions: Yes asserts the
// dim; Remediate / Compensate / Accept are mutually-exclusive ways to
// dispose of a gap, each producing distinct policy text. The "why"
// (alternative details and exception rationale) lives on the answer;
// remediation work is owned by the finding and its Task.
//
// Meanings are always visible (no hover/click to reveal) and grouped
// with their option so each label appears once. Remediate copy is the
// catalog-authored hint when present; Compensate / Accept are generic
// audit meaning. Required/Optional is a quiet text tag, not a colored
// badge, to keep blue the single accent.
// ─────────────────────────────────────────────────────────────────

// The four mutually-exclusive dispositions for a dimension. "Accept
// risk" carries different audit weight than Remediate / Compensate, so
// it stays a distinct option rather than folding into a generic "No".
const DIM_OPTIONS: Array<{ state: DimensionState; label: string }> = [
  { state: 'yes', label: 'In place' },
  { state: 'remediate', label: 'Needs work' },
  { state: 'compensate', label: 'Alternative control' },
  { state: 'accept', label: 'Request exception' },
]

// Shared fixed width for the radio option label column. Used by BOTH the
// parent question and the sub-question rows so every option's explanation
// starts at the same x across the whole questionnaire. Sized to fit the
// widest label ("Not applicable"); longer labels wrap within the column
// rather than pushing the explanation out of alignment.
const OPTION_LABEL_CLASS =
  'w-[7.5rem] shrink-0 font-body text-sm font-medium leading-relaxed'

function DimensionRow({
  policyRequirement = false,
  programId, blueprintId,
  followupOnly = false,
  gapDetailsLabel = 'What is missing?',
  exception, followup, loadingFollowups, workspaceId, savingException, exceptionError, onDetailChange, onSaveException, onSaveDetails,
  dimension,
  entry,
  optionalCommitment = false,
  onState,
  number,
  onAsk,
}: {
  policyRequirement?: boolean
  programId: string
  blueprintId: string
  followupOnly?: boolean
  gapDetailsLabel?: string
  exception?: PolicyExceptionRequestDTO
  followup?: PolicyQuestionDimensionFollowupDTO
  loadingFollowups: boolean
  workspaceId?: string
  savingException: boolean
  exceptionError: string | null
  onDetailChange: (key: string, updates: Partial<DimensionStateEntry>) => void
  onSaveException: (key: string, note: string) => Promise<void>
  onSaveDetails: (key: string, work?: QuestionnaireRemediationPlanDTO) => Promise<boolean>
  dimension: NonNullable<QuestionnaireBlueprintForRow['dimensions']>[number]
  entry?: DimensionStateEntry
  optionalCommitment?: boolean
  onState: (key: string, state: DimensionState | null) => void
  /** Stable sub-question ordinal (e.g. "2.1") for channel reference. */
  number?: string
  /** Opens Vera seeded with this sub-question's context. */
  onAsk?: () => void
}) {
  const required = Boolean(dimension.required)
  const current = entry?.state
  const savedException = exception?.rationale === entry?.note?.trim() ? exception : undefined
  const [editedPlan, setEditedPlan] = useState<string | undefined>()
  const [editedOwner, setEditedOwner] = useState<string | null | undefined>()
  const [editedDate, setEditedDate] = useState<string | null | undefined>()
  const [saved, setSaved] = useState(false)
  const [editingGap, setEditingGap] = useState(false)
  const [gapDraft, setGapDraft] = useState<QuestionnaireGapDraftDTO | null>(null)
  const [draftError, setDraftError] = useState<string | null>(null)
  const draftGapMutation = useMutation(trpc.readinessQuestionnaire.draftGapPlan.mutationOptions())
  const plan = editedPlan ?? followup?.remediationPlan ?? ''
  const ownerId = editedOwner === undefined ? followup?.ownerId ?? null : editedOwner
  const targetDate = editedDate === undefined ? followup?.dueDate?.slice(0, 10) ?? null : editedDate
  const completePlan = Boolean(entry?.note?.trim() && plan.trim() && ownerId && targetDate)
  const showGapForm = !followup?.taskId || editingGap

  const [testSearch, setTestSearch] = useState('')
  const debouncedTestSearch = useDebounce(testSearch, 250)
  const tests = useQuery({
    ...trpc.guidedTemplate.listAllTests.queryOptions({ search: debouncedTestSearch.trim() || undefined, limit: 20 }),
    enabled: current === 'compensate',
  })
  const selectedTestIds = entry?.alternativeTestIds ?? []
  const supportingTests = (tests.data?.tasks ?? []).filter((test) => test.evidenceCategory !== 'POLICY')
  const changeDetails = (updates: Partial<DimensionStateEntry>) => {
    setSaved(false)
    onDetailChange(dimension.key, updates)
  }

  // Each option's meaning, grouped with the option itself (no separate
  // legend, so labels appear once). Yes / Remediate copy is dimension-
  // specific; Compensate / Accept are the generic audit meaning, kept
  // terse so the repetition across sub-questions stays quiet.
  const tips: Record<DimensionState, string> = {
    yes: dimension.policyFragment,
    remediate: dimension.remediationHint ?? 'fix it with a target close date',
    compensate: 'an equivalent control that meets the intent',
    accept: 'request an exception for approval; the policy requirement stays in place until approved',
  }

  return (
    <div>
      {!followupOnly && !(policyRequirement && dimension.key === 'aggregate') && <div className='flex items-baseline justify-between gap-2'>
        <span className='font-body text-text-body min-w-0 flex-1 text-sm leading-snug font-semibold'>
          {number != null && (
            <span className='text-text-placeholder mr-1.5 font-normal tabular-nums'>
              {number}
            </span>
          )}
          {dimension.label}
          {/* "*" is the universal required marker — replaces the repeated
              "Required" tag. Optional dims are rarer, so they say so
              explicitly (a bare missing "*" would be ambiguous). */}
          {required && optionalCommitment ? (
            <span className='text-text-subtle ml-1.5 text-xs font-normal'>
              (needed if you include this practice)
            </span>
          ) : required && !policyRequirement ? (
            <span className='text-destructive ml-0.5' title='Required'>
              *
            </span>
          ) : !required ? (
            <span className='text-text-placeholder ml-1.5 font-normal'>
              (optional)
            </span>
          ) : null}
        </span>
        {onAsk && <AskVeraButton onClick={onAsk} />}
      </div>}
      {policyRequirement && !followupOnly && (
        <div className='mt-3 space-y-3'>
          {(savedException?.status === 'APPROVED' || (!required && !entry)) && <p className='text-text-subtle text-xs'>
            {savedException?.status === 'APPROVED' ? 'Exception approved. Regenerate to update the policy wording.' : 'Optional practice. Include it to add this commitment.'}
          </p>}
          <div className='flex flex-wrap items-center gap-x-4 gap-y-2'>
            {!required && entry?.state === 'yes' && <button type='button' disabled={savingException} className='text-text-subtle text-xs underline' onClick={() => onState(dimension.key, null)}>Remove optional practice</button>}
            {!required && !entry && <button type='button' disabled={savingException} className='text-cobalt-strong text-xs underline' onClick={() => onState(dimension.key, 'yes')}>Include optional practice</button>}
            {(required || entry) && <>
              <button type='button' disabled={savingException} className='text-text-subtle text-xs underline' onClick={() => onState(dimension.key, 'remediate')}>Report a gap</button>
              <button type='button' disabled={savingException} className='text-text-subtle text-xs underline' onClick={() => onState(dimension.key, 'compensate')}>Use a different safeguard</button>
              <button type='button' disabled={savingException} className='text-text-subtle text-xs underline' onClick={() => onState(dimension.key, 'accept')}>Request exception</button>
            </>}
          </div>
        </div>
      )}
      {policyRequirement && current !== 'remediate' && followup?.taskId && workspaceId && (
        <div className='border-border mt-3 space-y-1 rounded-md border p-3 text-sm'>
          <a className='text-cobalt-strong underline' href={`/workspaces/${workspaceId}/tasks/${followup.taskId}`}>View {followup.taskCode ?? 'remediation task'}</a>
          <p className='text-text-subtle text-xs'>{followup.taskStatus ? configForStatus(followup.taskStatus).label : 'Status unavailable'} · {followup.ownerName ?? 'Owner needed'} · {followup.dueDate?.slice(0, 10) ?? 'Target date needed'}</p>
          {followup.remediationPlan && <p className='text-text-body'>{followup.remediationPlan}</p>}
        </div>
      )}
      {/* One radio row per option — circle + label + meaning, the whole
          row clickable. Single-select; all four read as equal choices,
          labels share a fixed column so the meanings align down the
          page. */}
      <div
        className={followupOnly ? 'space-y-1.5' : '-mx-2.5 mt-3.5 space-y-1.5'}
        role={followupOnly || policyRequirement ? undefined : 'radiogroup'}
        aria-label={followupOnly || policyRequirement ? undefined : dimension.label}
      >
        {DIM_OPTIONS.map((option) => {
          // Only a real selection fills the radio — never pre-fill "Yes",
          // which would read as an answer the user didn't attest.
          const active = current === option.state
          if ((followupOnly || policyRequirement) && !active) return null
          return (
            <div key={option.state}>
              {!followupOnly && !policyRequirement && <button
                type='button'
                disabled={savingException}
                onClick={() => { setSaved(false); onState(dimension.key, option.state) }}
                role='radio'
                aria-checked={active}
                className={cn(
                  'flex w-full items-start gap-3 rounded-md px-2.5 py-2.5 text-left transition-colors',
                  active ? 'bg-cobalt-selected-faint' : 'hover:bg-background'
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                    active ? 'border-ring' : 'border-[#C4C4C2]'
                  )}
                >
                  {active && (
                    <span className='bg-primary h-1.5 w-1.5 rounded-full' />
                  )}
                </span>
                <span
                  className={cn(
                    OPTION_LABEL_CLASS,
                    active ? 'text-cobalt-strong' : 'text-text-body'
                  )}
                >
                  {option.label}
                </span>
                <span
                  className={cn(
                    'font-body text-xs leading-relaxed',
                    active ? 'text-text-body' : 'text-text-placeholder'
                  )}
                >
                  {tips[option.state]}
                  {option.state === 'yes' && (
                    <span className='ml-1 font-medium text-cobalt-strong'>
                      · recommended
                    </span>
                  )}
                </span>
              </button>}
              {active && option.state === 'remediate' && (
                <div className='border-border bg-muted/30 mx-2.5 mb-3 mt-3 space-y-3 rounded-md border p-3'>
                  <p className='text-text-body text-sm font-medium'>{followup ? 'Gap recorded' : 'Record a coverage gap'}</p>
                  {!showGapForm ? (
                    <>
                      <p className='text-text-body text-sm'>{followup?.taskCode} · {followup?.taskStatus ? configForStatus(followup.taskStatus).label : 'Status unavailable'}</p>
                      <p className='text-text-subtle text-xs'>{followup?.ownerName ?? 'Owner needed'} · {targetDate ? `Target date: ${targetDate}` : 'Target date needed'}</p>
                      {entry?.note?.trim() ? <p className='text-text-body whitespace-pre-wrap text-sm'>{entry.note}</p> : (
                        <div className='flex items-center gap-2 text-xs'>
                          <span className='text-status-amber-ink'>Gap details needed</span>
                          <button type='button' className='text-cobalt-strong underline' onClick={() => setEditingGap(true)}>Add gap details</button>
                        </div>
                      )}
                      {plan.trim() ? <p className='text-text-body whitespace-pre-wrap text-sm'>Remediation plan: {plan}</p> : <p className='text-status-amber-ink text-xs'>Remediation plan needed</p>}
                      <div className='flex flex-wrap items-center gap-3'>
                        {workspaceId && <a className='text-cobalt-strong text-sm underline' href={`/workspaces/${workspaceId}/tasks/${followup!.taskId}`}>View {followup?.taskCode ?? 'task'}</a>}
                        <button type='button' className='border-border rounded-md border px-3 py-1.5 text-sm' onClick={() => setEditingGap(true)}>Edit gap and plan</button>
                      </div>
                    </>
                  ) : (
                    <>
                      {!completePlan && <p className='text-status-amber-ink text-xs'>Remediation plan incomplete: add gap details, a plan, an owner, and a target date. You can still save the gap now.</p>}
                      <button type='button' disabled={savingException || loadingFollowups || draftGapMutation.isPending}
                        className='text-cobalt-strong inline-flex items-center gap-1.5 text-xs disabled:opacity-50'
                        onClick={async () => {
                          setDraftError(null)
                          setGapDraft(null)
                          try {
                            const draft = await draftGapMutation.mutateAsync({ programId, blueprintId, dimensionKey: dimension.key, gapDetails: entry?.note ?? '', remediationPlan: plan })
                            setGapDraft(draft)
                          } catch {
                            setDraftError('Vera could not draft this plan. Please retry or fill in the form manually.')
                          }
                        }}>
                        <Sparkles className='size-3.5' />{draftGapMutation.isPending ? 'Drafting…' : 'Draft with Vera'}
                      </button>
                      {draftError && <p role='alert' className='text-destructive text-xs'>{draftError}</p>}
                      {gapDraft && (
                        <section aria-label='Vera draft' className='border-border bg-background space-y-2 rounded-md border p-3'>
                          <p className='text-text-body text-xs font-medium'>Review Vera’s draft</p>
                          <p className='text-text-subtle text-xs'>Apply the suggestions to the form, then review and save. Owner and target date stay as you set them.</p>
                          {gapDraft.gapDetails && <p className='text-text-body whitespace-pre-wrap text-sm'>Gap details: {gapDraft.gapDetails}</p>}
                          <p className='text-text-body whitespace-pre-wrap text-sm'>Remediation plan: {gapDraft.remediationPlan}</p>
                          {gapDraft.questions.length > 0 && <div className='text-text-subtle text-xs'><p>Details to confirm:</p><ul className='list-disc pl-4'>{gapDraft.questions.map((question, index) => <li key={index}>{question}</li>)}</ul></div>}
                          <div className='flex gap-3'>
                            <button type='button' disabled={savingException} className='text-cobalt-strong text-xs underline' onClick={() => {
                              changeDetails(gapDraft.gapDetails === null ? {} : { note: gapDraft.gapDetails })
                              setEditedPlan(gapDraft.remediationPlan)
                              setGapDraft(null)
                            }}>Apply draft</button>
                            <button type='button' className='text-text-subtle text-xs' onClick={() => setGapDraft(null)}>Dismiss draft</button>
                          </div>
                        </section>
                      )}
                      <label className='text-text-secondary block text-xs'>{gapDetailsLabel}
                        <textarea aria-label={`Gap details: ${dimension.label}`} rows={2} maxLength={4000} value={entry?.note ?? ''}
                          disabled={savingException} onChange={(event) => changeDetails({ note: event.target.value })}
                          className='border-border bg-background text-text-body mt-1 block w-full rounded-md border p-2 text-sm'
                          placeholder='Describe the missing coverage or current limitation.' />
                      </label>
                      <label className='text-text-secondary block text-xs'>Remediation plan
                        <textarea aria-label={`Remediation plan: ${dimension.label}`} rows={2} maxLength={4000} value={plan}
                          disabled={savingException || loadingFollowups} onChange={(event) => { changeDetails({}); setEditedPlan(event.target.value) }}
                          className='border-border bg-background text-text-body mt-1 block w-full rounded-md border p-2 text-sm'
                          placeholder={dimension.remediationHint ?? 'Describe the steps to put this practice in place.'} />
                      </label>
                      <div className='grid gap-3 sm:grid-cols-2'>
                        <div><p className='text-text-secondary mb-1 text-xs'>Owner</p>
                          <AssignCombobox currentAssigneeId={ownerId} showName modal excludeAgent readOnly={savingException || loadingFollowups}
                            onValueChange={(value) => { changeDetails({}); setEditedOwner(value) }} />
                        </div>
                        <label className='text-text-secondary block text-xs'>Target date
                          <input aria-label={`Remediation target date: ${dimension.label}`} type='date' value={targetDate ?? ''}
                            disabled={savingException || loadingFollowups} onChange={(event) => { changeDetails({}); setEditedDate(event.target.value || null) }}
                            className='border-border bg-background text-text-body mt-1 block w-full rounded-md border p-2 text-sm' />
                        </label>
                      </div>
                      <p className='text-text-subtle text-xs'>This requirement stays in the policy. Saving creates or updates the linked remediation task. Add evidence to close the gap.</p>
                      {exceptionError && <p role='alert' className='text-destructive text-xs'>{exceptionError}</p>}
                      <div className='flex flex-wrap items-center gap-3'>
                        <button type='button' disabled={savingException || loadingFollowups}
                          onClick={async () => {
                            const success = await onSaveDetails(dimension.key, { remediationPlan: plan, ownerId, dueDate: targetDate })
                            setSaved(success)
                            if (success) setEditingGap(false)
                          }}
                          className='border-border rounded-md border px-3 py-1.5 text-sm disabled:opacity-50'>{savingException ? 'Saving…' : 'Save gap and plan'}</button>
                        {policyRequirement && <button type='button' disabled={savingException} className='text-text-subtle rounded-md px-3 py-1.5 text-sm hover:bg-surface-muted disabled:opacity-50'
                          onClick={() => { setSaved(false); setEditingGap(false); setEditedPlan(undefined); setEditedOwner(undefined); setEditedDate(undefined); setGapDraft(null); setDraftError(null); onState(dimension.key, 'yes') }}>Cancel and keep requirement</button>}
                        {saved && <span role='status' className='text-text-subtle text-xs'>Saved</span>}
                        {followup?.taskId && workspaceId && <a className='text-cobalt-strong text-sm underline' href={`/workspaces/${workspaceId}/tasks/${followup.taskId}`}>View {followup.taskCode} and remediation</a>}
                      </div>
                    </>
                  )}
                </div>
              )}
              {active && option.state === 'compensate' && (
                <div className='border-border bg-muted/30 mx-2.5 mb-3 mt-3 space-y-3 rounded-md border p-3'>
                  <p className='text-text-body text-sm font-medium'>Your alternative safeguard</p>
                  <p className='text-text-subtle text-xs'>Describe what you use instead. This will appear in the policy; linked tests will check whether it works.</p>
                  <label className='text-text-secondary block text-xs'>What do you use instead? (required)
                    <textarea aria-label={`Alternative safeguard: ${dimension.label}`} rows={2} maxLength={4000} value={entry?.note ?? ''}
                      disabled={savingException} onChange={(event) => changeDetails({ note: event.target.value })}
                      className='border-border bg-background text-text-body mt-1 block w-full rounded-md border p-2 text-sm'
                      placeholder='Describe your process or technical safeguard in at least 20 characters.' />
                  </label>
                  <label className='text-text-secondary block text-xs'>What does it cover? (required)
                    <textarea aria-label={`Alternative scope: ${dimension.label}`} rows={2} maxLength={4000} value={entry?.alternativeScope ?? ''}
                      disabled={savingException} onChange={(event) => changeDetails({ alternativeScope: event.target.value })}
                      className='border-border bg-background text-text-body mt-1 block w-full rounded-md border p-2 text-sm'
                      placeholder='Which systems, accounts, or access paths does it cover?' />
                  </label>
                  <label className='text-text-secondary block text-xs'>How does it provide the same protection? (required)
                    <textarea aria-label={`Alternative rationale: ${dimension.label}`} rows={2} maxLength={4000} value={entry?.alternativeRationale ?? ''}
                      disabled={savingException} onChange={(event) => changeDetails({ alternativeRationale: event.target.value })}
                      className='border-border bg-background text-text-body mt-1 block w-full rounded-md border p-2 text-sm'
                      placeholder='Explain how it addresses this requirement and any remaining gaps (at least 20 characters).' />
                  </label>
                  <fieldset className='space-y-2'>
                    <legend className='text-text-secondary text-xs'>Supporting tests (optional)</legend>
                    {selectedTestIds.map((testId) => {
                      const test = tests.data?.tasks.find((candidate) => candidate.statusId === testId)
                      return <div key={testId} className='flex items-center justify-between gap-2 text-xs'>
                        <a className='text-cobalt-strong underline' href={`/workspaces/${workspaceId}/tests/${testId}`}>{test ? `${test.displayId ?? 'Test'} · ${test.title}` : 'View selected test'}</a>
                        <button type='button' disabled={savingException} className='text-text-subtle underline' aria-label={`Remove supporting test ${test?.displayId ?? test?.title ?? ''}`}
                          onClick={() => changeDetails({ alternativeTestIds: selectedTestIds.filter((id) => id !== testId) })}>Remove</button>
                      </div>
                    })}
                    <input aria-label={`Search supporting tests: ${dimension.label}`} value={testSearch} onChange={(event) => setTestSearch(event.target.value)}
                      placeholder='Search tests by code or title' className='border-border bg-background text-text-body block w-full rounded-md border p-2 text-sm' />
                    {tests.isError ? <p role='alert' className='text-destructive text-xs'>Could not load tests. Retry the search before selecting one.</p> : tests.isLoading ? <p className='text-text-subtle text-xs'>Loading tests…</p> : (
                      <div className='max-h-40 space-y-2 overflow-y-auto'>
                        {supportingTests.map((test) => <label key={test.statusId} className='text-text-body flex items-start gap-2 text-xs'>
                          <input type='checkbox' checked={selectedTestIds.includes(test.statusId)} disabled={savingException || (!selectedTestIds.includes(test.statusId) && selectedTestIds.length >= 20)}
                            onChange={(event) => changeDetails({ alternativeTestIds: event.target.checked ? [...selectedTestIds, test.statusId] : selectedTestIds.filter((id) => id !== test.statusId) })} />
                          <span>{test.displayId ?? 'Test'} · {test.title}</span>
                        </label>)}
                        {!supportingTests.length && <p className='text-text-subtle text-xs'>No matching tests. You can save the description and link a test later in Claims.</p>}
                      </div>
                    )}
                  </fieldset>
                  <p className='text-text-subtle text-xs'>Regeneration describes this specific safeguard and links the selected tests to its claim. Evidence review is still needed. An incomplete description keeps the original required practice.</p>
                  {exceptionError && <p role='alert' className='text-destructive text-xs'>{exceptionError}</p>}
                  <div className='flex flex-wrap items-center gap-3'>
                    <button type='button' disabled={savingException || !hasAlternativeControlDetails(entry)}
                      onClick={async () => setSaved(await onSaveDetails(dimension.key))}
                      className='border-border rounded-md border px-3 py-1.5 text-sm disabled:opacity-50'>{savingException ? 'Saving…' : 'Save alternative safeguard'}</button>
                        {policyRequirement && <button type='button' disabled={savingException} className='text-text-subtle rounded-md px-3 py-1.5 text-sm hover:bg-surface-muted disabled:opacity-50'
                          onClick={() => { setSaved(false); setEditingGap(false); setEditedPlan(undefined); setEditedOwner(undefined); setEditedDate(undefined); setGapDraft(null); setDraftError(null); onState(dimension.key, 'yes') }}>Cancel and keep requirement</button>}
                    {saved && <span role='status' className='text-text-subtle text-xs'>Saved</span>}
                  </div>
                </div>
              )}

              {active && option.state === 'accept' && (
                <div className='border-border bg-muted/30 mx-2.5 mb-3 mt-3 space-y-3 rounded-md border p-3'>
                  <p className='text-text-body text-sm font-medium'>
                    {savedException?.status === 'APPROVED' ? 'Exception approved' : savedException?.status === 'PENDING' ? 'Exception requested' : savedException?.status === 'EXPIRED' ? 'Acceptance expired' : savedException?.status === 'REVOKED' ? 'Exception not approved' : 'Unsaved exception request'}
                  </p>
                  <label className='text-text-secondary block text-xs'>
                    Rationale (required, at least 20 characters)
                    <textarea aria-label={`Exception rationale: ${dimension.label}`} value={entry?.note ?? ''}
                      onChange={(event) => changeDetails({ note: event.target.value })}
                      maxLength={4000} rows={3} disabled={savingException}
                      className='border-border bg-background text-text-body mt-1 block w-full rounded-md border p-2 text-sm'
                      placeholder='Explain the scope, business reason, and any safeguards for this exception.' />
                  </label>
                  <p className='text-text-subtle text-xs'>Saving submits this exception request to the risk register. Other requests are saved separately. Approval requires an authorized decision and an expiry date. Regeneration reuses the decision.</p>
                  {exceptionError && <p role='alert' className='text-destructive text-xs'>{exceptionError}</p>}
                  <div className='flex flex-wrap items-center gap-3'>
                    {!savedException && <button type='button' disabled={savingException || (entry.note?.trim().length ?? 0) < 20}
                      onClick={() => void onSaveException(dimension.key, entry.note ?? '')}
                      className='border-border rounded-md border px-3 py-1.5 text-sm disabled:opacity-50'>
                      {savingException ? 'Saving…' : exception ? 'Save request changes' : 'Save exception request'}
                    </button>}
                    {policyRequirement && <button type='button' disabled={savingException} className='text-text-subtle rounded-md px-3 py-1.5 text-sm hover:bg-surface-muted disabled:opacity-50'
                          onClick={() => { setSaved(false); setEditingGap(false); setEditedPlan(undefined); setEditedOwner(undefined); setEditedDate(undefined); setGapDraft(null); setDraftError(null); onState(dimension.key, 'yes') }}>Cancel and keep requirement</button>}
                    {savedException && workspaceId && <a href={`/workspaces/${workspaceId}/risks/${savedException.riskId}`} className='text-cobalt-strong text-sm underline'>View {savedException.displayId} and approval</a>}
                  </div>
                  {(savedException?.status === 'EXPIRED' || savedException?.status === 'REVOKED') && <p className='text-text-subtle text-xs'>Request a new approval and expiry in the linked risk entry. The policy requirement remains in place.</p>}
                </div>
              )}
            </div>
          )
        })}
      </div>

    </div>
  )
}

// Quiet per-question / per-sub-question affordance to open Vera seeded
// with that item's context. Low-emphasis until hovered so it never
// competes with the answer radios.
function AskVeraButton({
  onClick,
  className,
}: {
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        'font-body text-text-placeholder inline-flex shrink-0 items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium transition-colors hover:bg-cobalt-xsubtle hover:text-cobalt-strong',
        className
      )}
    >
      <Sparkles className='h-3 w-3' />
      Ask Vera
    </button>
  )
}

// RFC-172 Stage 7 / Phase E — provenance indicator for answers
// pre-filled from a policy upload. Two tiers:
//   high   — overallConfidence ≥ 0.9 + 'imported' source. Auto-applied
//            from the extraction. Founder review optional.
//   review — confidence 0.7-0.9 OR explicit-accept of a sub-threshold
//            extraction. Surfaced to prompt founder verification.
function ImportedBadge({
  tier,
  importMeta,
}: {
  tier: 'high' | 'review'
  importMeta: NonNullable<RowInitialState['importMeta']>
}) {
  const isReview = tier === 'review'
  const confidence = importMeta.overallConfidence
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-1 rounded-md px-2 text-xs font-medium tracking-wide uppercase',
        isReview ? 'bg-status-amber-badge-surface text-status-amber-ink' : 'bg-cobalt-xsubtle text-status-blue-strong'
      )}
      title={`Pre-filled from your uploaded policy (confidence ${(
        confidence * 100
      ).toFixed(
        0
      )}%). ${isReview ? 'Borderline, please verify.' : 'High confidence.'}`}
    >
      <Sparkles className='h-3 w-3' />
      {isReview ? 'Imported · Review' : 'Imported'}
    </span>
  )
}

function inferAttestationAnswer(optionKey: string): AttestationAnswerValue {
  const k = optionKey.toLowerCase().replace(/[\s_\-']+/g, '')
  if (['yes', 'y', 'true'].includes(k)) return 'YES'
  if (['partial', 'partially'].includes(k)) return 'PARTIAL'
  if (['no', 'n', 'false'].includes(k)) return 'NO'
  if (['na', 'notapplicable'].includes(k)) return 'NA'
  if (['unknown', 'idontknow', 'dontknow'].includes(k)) return 'UNKNOWN'
  // Conservative fallback — server normalizes via answerAliases anyway.
  return 'YES'
}
